import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';
import wordmarkUrl from './wundo-wordmark.png';
import registeredWordmarkUrl from './wundo-wordmark-registered.png';

/*
 * A mask rather than an image, so the colour comes from a token (green would
 * vanish on the surface). A mask has no size of its own, so the aspect ratio
 * sizes it. Product chrome omits the ®; `registered` is the full artwork the
 * standalone screens carry, in brand black.
 */
export const wordmarkVariants = cva(
  [
    'block w-auto shrink-0 aspect-[1138/297] bg-ink',
    '[mask-image:var(--wordmark)] [mask-size:contain]',
    '[mask-repeat:no-repeat] [mask-position:left_center]',
  ],
  {
    variants: {
      size: {
        md: 'h-3.5',
        lg: 'h-4.25',
      },
      registered: {
        true: 'aspect-[1200/297] bg-brand-black',
        false: '',
      },
    },
    defaultVariants: {
      size: 'lg',
      registered: false,
    },
  },
);

export type WordmarkVariants = VariantProps<typeof wordmarkVariants>;

export interface WordmarkProps extends WordmarkVariants {
  className?: string;
  title?: string;
}

export function Wordmark({
  className,
  size,
  registered,
  title = 'Wundo',
}: WordmarkProps) {
  const url = registered ? registeredWordmarkUrl : wordmarkUrl;

  return (
    <span
      data-slot='wordmark'
      role='img'
      aria-label={title}
      style={{ '--wordmark': `url(${url})` } as React.CSSProperties}
      className={cn(wordmarkVariants({ size, registered }), className)}
    />
  );
}
